export enum ConditionDataType {
  NUMBER = 'number',
  TEXT = 'text',
  DATE = 'date',
  MULTI_SELECT = 'multi_select',
  BOOLEAN = 'boolean',
  DROPDOWN = 'dropdown',
}

export enum ActionInputType {
  NONE = 'none',
  TEXT = 'text',
  TEXTAREA = 'textarea',
  NUMBER = 'number',
  DATE = 'date',
  DROPDOWN = 'dropdown',
  MULTI_SELECT = 'multi_select',
}

export enum ConditionField {
  // Number fields
  ORDER_VALUE = 'order_value',
  ITEM_QUANTITY = 'item_quantity',
  PACKAGE_WEIGHT_OZ = 'package_weight_oz',
  PACKAGE_COUNT = 'package_count',

  // Text fields
  ORDER_NUMBER_REF1 = 'order_number_ref1',
  ITEM_SKU = 'item_sku',
  ITEM_NAME = 'item_name',
  REF_1 = 'ref_1',
  REF_2 = 'ref_2',
  ORDER_NOTES = 'order_notes',
  CUSTOMER_NAME = 'customer_name',
  CUSTOMER_EMAIL = 'customer_email',
  DESTINATION_STATE = 'destination_state',
  POSTAL_CODE = 'postal_code',

  // Date fields
  ORDER_DATE = 'order_date',
  SHIP_DATE = 'ship_date',

  // Multi-select fields
  MARKETPLACE = 'marketplace',
  STORE_NAME = 'store_name',
  MERCHANDISE_CATEGORY = 'merchandise_category',
  DESTINATION_COUNTRY = 'destination_country',
  CARRIER = 'carrier',
  SHIPPING_SERVICE = 'shipping_service',

  // Boolean fields
  RESIDENTIAL = 'residential',
  INTERNATIONAL_ORDER = 'international_order',

  // Dropdown fields
  SHIP_FROM_LOCATION = 'ship_from_location',
  PACKAGE_DIMENSIONS = 'package_dimensions',
}

export enum NumberOperator {
  EQUALS = 'equals',
  DOES_NOT_EQUAL = 'does_not_equal',
  GREATER_THAN = 'greater_than',
  GREATER_THAN_OR_EQUAL = 'greater_than_or_equal',
  LESS_THAN = 'less_than',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
}

export enum TextOperator {
  EQUALS = 'equals',
  DOES_NOT_EQUAL = 'does_not_equal',
  CONTAINS = 'contains',
  DOES_NOT_CONTAIN = 'does_not_contain',
  STARTS_WITH = 'starts_with',
  DOES_NOT_START_WITH = 'does_not_start_with',
  IS_BLANK = 'is_blank',
  IS_NOT_BLANK = 'is_not_blank',
}

export enum DateOperator {
  EQUALS_CURRENT_DATE = 'equals_current_date',
  BETWEEN = 'between',
  IS_ON_OR_AFTER = 'is_on_or_after',
  IS_ON_OR_BEFORE = 'is_on_or_before',
}

export enum MultiSelectOperator {
  INCLUDES = 'includes',
  IS_BLANK = 'is_blank',
  IS_NOT_BLANK = 'is_not_blank',
}

export enum BooleanOperator {
  EQUALS = 'equals',
}

export enum DropdownOperator {
  EQUALS = 'equals',
  DOES_NOT_EQUAL = 'does_not_equal',
}

export type ConditionOperator =
  | NumberOperator
  | TextOperator
  | DateOperator
  | MultiSelectOperator
  | BooleanOperator
  | DropdownOperator

export enum ActionType {
  SELECT_CHEAPEST_SERVICE = 'select_cheapest_service',
  SET_SHIPPING_SERVICE = 'set_shipping_service',
  SET_PACKAGE_TYPE = 'set_package_type',
  SET_PACKAGE_DIMENSION = 'set_package_dimension',
  SET_WEIGHT = 'set_weight',
  SET_CONFIRMATION_TYPE = 'set_confirmation_type',
  HAZMAT_SERVICE_OPTION = 'hazmat_service_option',
  APPLY_PRESET = 'apply_preset',
  INSURE_PACKAGE = 'insure_package',
  ADD_TAG = 'add_tag',
  ASSIGN_FROM_ADDRESS = 'assign_from_address',
  HOLD_ORDER = 'hold_order',
  ADD_ORDER_NOTE = 'add_order_note',
  SET_REF_1 = 'set_ref_1',
  SET_REF_2 = 'set_ref_2',
  SET_SHIP_BY_DATE = 'set_ship_by_date',
}

export enum OptionsSource {
  MARKETPLACES = 'marketplaces',
  STORES = 'stores',
  TAGS = 'tags',
  CARRIERS = 'carriers',
  SHIPPING_SERVICES = 'shipping_services',
  PACKAGE_TYPES = 'package_types',
  CONFIRMATION_TYPES = 'confirmation_types',
  HAZMAT_CODES = 'hazmat_codes',
  PRESETS = 'presets',
  INSURANCE_TYPES = 'insurance_types',
  SHIP_FROM_ADDRESSES = 'ship_from_addresses',
  PACKAGES = 'packages',
}

export interface ShipFromLocationValue {
  shipping_address_id: number
  from_company: string | null
  from_first_name: string | null
  from_last_name: string | null
  from_street1: string
  from_street2: string | null
  from_city: string
  from_state: string
  from_zip: string
  from_country: string
  from_phone: string | null
  from_email: string | null
}

export interface PackageDimensionsValue {
  package_id: number
  length: number
  width: number
  height: number
  dimension_unit: string
}

export interface SetShippingServiceValue {
  service: string
}

export interface SetPackageTypeValue {
  package_id: number | null
  package_type: string
  length: number
  width: number
  height: number
  dimension_unit: string
}

export interface SetPackageDimensionValue {
  length: number | null
  width: number | null
  height: number | null
  dimension_unit: string
}

export interface SetWeightValue {
  weight: number
  weight_unit: string
}

export interface SetConfirmationTypeValue {
  confirmation_type: string
}

export interface HazmatServiceOptionValue {
  hazmat_code: number
}

export interface ApplyPresetValue {
  preset_id: number
}

export interface InsurePackageValue {
  insurance_type: string
}

export interface AddTagValue {
  tag_ids: number[]
}

export interface AssignFromAddressValue {
  shipping_address_id: number
  from_company: string | null
  from_first_name: string | null
  from_last_name: string | null
  from_street1: string
  from_street2: string | null
  from_city: string
  from_state: string
  from_zip: string
  from_country: string
  from_phone: string | null
  from_email: string | null
}

export interface HoldOrderValue {
  hold_until_date: string
}

export interface AddOrderNoteValue {
  note: string
}

export interface SetRef1Value {
  reference1: string
}

export interface SetRef2Value {
  reference2: string
}

export interface SetShipByDateValue {
  ship_by_date: string
}

export type ActionValue =
  | null
  | SetShippingServiceValue
  | SetPackageTypeValue
  | SetPackageDimensionValue
  | SetWeightValue
  | SetConfirmationTypeValue
  | HazmatServiceOptionValue
  | ApplyPresetValue
  | InsurePackageValue
  | AddTagValue
  | AssignFromAddressValue
  | HoldOrderValue
  | AddOrderNoteValue
  | SetRef1Value
  | SetRef2Value
  | SetShipByDateValue

export interface Condition {
  field: ConditionField
  operator: ConditionOperator
  value:
    | string
    | number
    | boolean
    | string[]
    | number[]
    | ShipFromLocationValue
    | PackageDimensionsValue
    | null
  value_to: string | number | null
}

export interface ConditionsSerialized {
  aggregator: 'all'
  conditions: Condition[]
}

export interface Action {
  type: ActionType
  value: ActionValue
}

export interface ActionsSerialized {
  actions: Action[]
}

export interface AutomationRule {
  id: number
  account_id: number
  name: string
  description: string | null
  is_active: boolean
  priority: number | null
  conditions_serialized: ConditionsSerialized
  actions_serialized: ActionsSerialized
  created_at: string
  updated_at: string
}

export interface ConditionFieldMetadata {
  field: ConditionField
  label: string
  data_type: ConditionDataType
  operators: ConditionOperator[]
  options_source: OptionsSource | null
}

export interface ActionTypeMetadata {
  type: ActionType
  label: string
  input_type: ActionInputType
  value_schema: Record<string, string> | null
  options_source: OptionsSource | null
  supports_multiple: boolean
}

export interface AutomationRulesMetadata {
  conditions: ConditionFieldMetadata[]
  actions: ActionTypeMetadata[]
}

export interface CreateAutomationRulePayload {
  name: string
  description?: string | null
  is_active: boolean
  conditions_serialized: ConditionsSerialized
  actions_serialized: ActionsSerialized
}

export interface UpdateAutomationRulePayload extends CreateAutomationRulePayload {}

export interface UpdateAutomationRuleStatusPayload {
  is_active: boolean
}

export interface ReorderAutomationRulesPayload {
  rule_ids: number[]
}

export interface AutomationRuleResponse {
  automation_rule: AutomationRule
}

export interface AutomationRulesListResponse {
  automation_rules: AutomationRule[]
}

export interface AutomationRulesMetadataResponse {
  conditions: ConditionFieldMetadata[]
  actions: ActionTypeMetadata[]
}

export interface DeleteAutomationRuleResponse {
  success: boolean
}

export interface ReorderAutomationRulesResponse {
  success: boolean
}

export interface ValidationError {
  message: string
  errors: Record<string, string[]>
}

export const AUTOMATION_RULES_LIMITS = {
  MAX_RULES_PER_ACCOUNT: 100,
  MAX_NAME_LENGTH: 50,
  MAX_DESCRIPTION_LENGTH: 50,
  MIN_ACTIONS: 1,
} as const

export const AGGREGATOR_DEFAULT = 'all' as const

export const NUMBER_FIELDS: ConditionField[] = [
  ConditionField.ORDER_VALUE,
  ConditionField.ITEM_QUANTITY,
  ConditionField.PACKAGE_WEIGHT_OZ,
  ConditionField.PACKAGE_COUNT,
]

export const TEXT_FIELDS: ConditionField[] = [
  ConditionField.ORDER_NUMBER_REF1,
  ConditionField.ITEM_SKU,
  ConditionField.ITEM_NAME,
  ConditionField.REF_1,
  ConditionField.REF_2,
  ConditionField.ORDER_NOTES,
  ConditionField.CUSTOMER_NAME,
  ConditionField.CUSTOMER_EMAIL,
  ConditionField.DESTINATION_STATE,
  ConditionField.POSTAL_CODE,
]

export const DATE_FIELDS: ConditionField[] = [ConditionField.ORDER_DATE, ConditionField.SHIP_DATE]

export const MULTI_SELECT_FIELDS: ConditionField[] = [
  ConditionField.MARKETPLACE,
  ConditionField.STORE_NAME,
  ConditionField.MERCHANDISE_CATEGORY,
  ConditionField.DESTINATION_COUNTRY,
  ConditionField.CARRIER,
  ConditionField.SHIPPING_SERVICE,
]

export const BOOLEAN_FIELDS: ConditionField[] = [
  ConditionField.RESIDENTIAL,
  ConditionField.INTERNATIONAL_ORDER,
]

export const DROPDOWN_FIELDS: ConditionField[] = [
  ConditionField.SHIP_FROM_LOCATION,
  ConditionField.PACKAGE_DIMENSIONS,
]

export const NUMBER_OPERATORS: NumberOperator[] = [
  NumberOperator.EQUALS,
  NumberOperator.DOES_NOT_EQUAL,
  NumberOperator.GREATER_THAN,
  NumberOperator.GREATER_THAN_OR_EQUAL,
  NumberOperator.LESS_THAN,
  NumberOperator.LESS_THAN_OR_EQUAL,
]

export const TEXT_OPERATORS: TextOperator[] = [
  TextOperator.EQUALS,
  TextOperator.DOES_NOT_EQUAL,
  TextOperator.CONTAINS,
  TextOperator.DOES_NOT_CONTAIN,
  TextOperator.STARTS_WITH,
  TextOperator.DOES_NOT_START_WITH,
  TextOperator.IS_BLANK,
  TextOperator.IS_NOT_BLANK,
]

export const DATE_OPERATORS: DateOperator[] = [
  DateOperator.EQUALS_CURRENT_DATE,
  DateOperator.BETWEEN,
  DateOperator.IS_ON_OR_AFTER,
  DateOperator.IS_ON_OR_BEFORE,
]

export const MULTI_SELECT_OPERATORS: MultiSelectOperator[] = [
  MultiSelectOperator.INCLUDES,
  MultiSelectOperator.IS_BLANK,
  MultiSelectOperator.IS_NOT_BLANK,
]

export const BOOLEAN_OPERATORS: BooleanOperator[] = [BooleanOperator.EQUALS]

export const DROPDOWN_OPERATORS: DropdownOperator[] = [
  DropdownOperator.EQUALS,
  DropdownOperator.DOES_NOT_EQUAL,
]

export const OPERATORS_REQUIRING_NULL_VALUE: ConditionOperator[] = [
  TextOperator.IS_BLANK,
  TextOperator.IS_NOT_BLANK,
  MultiSelectOperator.IS_BLANK,
  MultiSelectOperator.IS_NOT_BLANK,
  DateOperator.EQUALS_CURRENT_DATE,
]

export const OPERATORS_USING_VALUE_TO: ConditionOperator[] = [DateOperator.BETWEEN]

export const FIELD_TO_DATA_TYPE_MAP: Record<ConditionField, ConditionDataType> = {
  [ConditionField.ORDER_VALUE]: ConditionDataType.NUMBER,
  [ConditionField.ITEM_QUANTITY]: ConditionDataType.NUMBER,
  [ConditionField.PACKAGE_WEIGHT_OZ]: ConditionDataType.NUMBER,
  [ConditionField.PACKAGE_COUNT]: ConditionDataType.NUMBER,
  [ConditionField.ORDER_NUMBER_REF1]: ConditionDataType.TEXT,
  [ConditionField.ITEM_SKU]: ConditionDataType.TEXT,
  [ConditionField.ITEM_NAME]: ConditionDataType.TEXT,
  [ConditionField.REF_1]: ConditionDataType.TEXT,
  [ConditionField.REF_2]: ConditionDataType.TEXT,
  [ConditionField.ORDER_NOTES]: ConditionDataType.TEXT,
  [ConditionField.CUSTOMER_NAME]: ConditionDataType.TEXT,
  [ConditionField.CUSTOMER_EMAIL]: ConditionDataType.TEXT,
  [ConditionField.DESTINATION_STATE]: ConditionDataType.TEXT,
  [ConditionField.POSTAL_CODE]: ConditionDataType.TEXT,
  [ConditionField.ORDER_DATE]: ConditionDataType.DATE,
  [ConditionField.SHIP_DATE]: ConditionDataType.DATE,
  [ConditionField.MARKETPLACE]: ConditionDataType.MULTI_SELECT,
  [ConditionField.STORE_NAME]: ConditionDataType.MULTI_SELECT,
  [ConditionField.MERCHANDISE_CATEGORY]: ConditionDataType.MULTI_SELECT,
  [ConditionField.DESTINATION_COUNTRY]: ConditionDataType.MULTI_SELECT,
  [ConditionField.CARRIER]: ConditionDataType.MULTI_SELECT,
  [ConditionField.SHIPPING_SERVICE]: ConditionDataType.MULTI_SELECT,
  [ConditionField.RESIDENTIAL]: ConditionDataType.BOOLEAN,
  [ConditionField.INTERNATIONAL_ORDER]: ConditionDataType.BOOLEAN,
  [ConditionField.SHIP_FROM_LOCATION]: ConditionDataType.DROPDOWN,
  [ConditionField.PACKAGE_DIMENSIONS]: ConditionDataType.DROPDOWN,
}

export const FIELD_TO_OPERATORS_MAP: Record<ConditionField, ConditionOperator[]> = {
  [ConditionField.ORDER_VALUE]: NUMBER_OPERATORS,
  [ConditionField.ITEM_QUANTITY]: NUMBER_OPERATORS,
  [ConditionField.PACKAGE_WEIGHT_OZ]: NUMBER_OPERATORS,
  [ConditionField.PACKAGE_COUNT]: NUMBER_OPERATORS,
  [ConditionField.ORDER_NUMBER_REF1]: TEXT_OPERATORS,
  [ConditionField.ITEM_SKU]: TEXT_OPERATORS,
  [ConditionField.ITEM_NAME]: TEXT_OPERATORS,
  [ConditionField.REF_1]: TEXT_OPERATORS,
  [ConditionField.REF_2]: TEXT_OPERATORS,
  [ConditionField.ORDER_NOTES]: TEXT_OPERATORS,
  [ConditionField.CUSTOMER_NAME]: TEXT_OPERATORS,
  [ConditionField.CUSTOMER_EMAIL]: TEXT_OPERATORS,
  [ConditionField.DESTINATION_STATE]: TEXT_OPERATORS,
  [ConditionField.POSTAL_CODE]: TEXT_OPERATORS,
  [ConditionField.ORDER_DATE]: DATE_OPERATORS,
  [ConditionField.SHIP_DATE]: DATE_OPERATORS,
  [ConditionField.MARKETPLACE]: MULTI_SELECT_OPERATORS,
  [ConditionField.STORE_NAME]: MULTI_SELECT_OPERATORS,
  [ConditionField.MERCHANDISE_CATEGORY]: MULTI_SELECT_OPERATORS,
  [ConditionField.DESTINATION_COUNTRY]: MULTI_SELECT_OPERATORS,
  [ConditionField.CARRIER]: MULTI_SELECT_OPERATORS,
  [ConditionField.SHIPPING_SERVICE]: MULTI_SELECT_OPERATORS,
  [ConditionField.RESIDENTIAL]: BOOLEAN_OPERATORS,
  [ConditionField.INTERNATIONAL_ORDER]: BOOLEAN_OPERATORS,
  [ConditionField.SHIP_FROM_LOCATION]: DROPDOWN_OPERATORS,
  [ConditionField.PACKAGE_DIMENSIONS]: DROPDOWN_OPERATORS,
}

export const requiresNullValue = (operator: ConditionOperator): boolean => {
  return OPERATORS_REQUIRING_NULL_VALUE.includes(operator)
}

export const usesValueTo = (operator: ConditionOperator): boolean => {
  return OPERATORS_USING_VALUE_TO.includes(operator)
}

export const getFieldDataType = (field: ConditionField): ConditionDataType => {
  return FIELD_TO_DATA_TYPE_MAP[field]
}

export const getFieldOperators = (field: ConditionField): ConditionOperator[] => {
  return FIELD_TO_OPERATORS_MAP[field]
}

export const isValidFieldOperatorCombination = (
  field: ConditionField,
  operator: ConditionOperator
): boolean => {
  const validOperators = FIELD_TO_OPERATORS_MAP[field]
  return validOperators.includes(operator)
}

export const createEmptyCondition = (field?: ConditionField): Partial<Condition> => {
  if (!field) {
    return {
      field: undefined,
      operator: undefined,
      value: null,
      value_to: null,
    }
  }

  const operators = getFieldOperators(field)
  return {
    field,
    operator: operators[0],
    value: null,
    value_to: null,
  }
}

export const createEmptyConditionsSerialized = (): ConditionsSerialized => {
  return {
    aggregator: AGGREGATOR_DEFAULT,
    conditions: [],
  }
}

export const createEmptyAction = (type?: ActionType): Partial<Action> => {
  if (!type) {
    return {
      type: undefined,
      value: null,
    }
  }

  return {
    type,
    value: null,
  }
}

export const createEmptyActionsSerialized = (): ActionsSerialized => {
  return {
    actions: [],
  }
}

export const createEmptyAutomationRule = (): Partial<CreateAutomationRulePayload> => {
  return {
    name: '',
    description: null,
    is_active: true,
    conditions_serialized: createEmptyConditionsSerialized(),
    actions_serialized: createEmptyActionsSerialized(),
  }
}
